/**
 * CONSAI Business App - Professional GST-Compliant Invoice Management
 * Refactored to use modular components
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Receipt, Plus, Trash2, ChevronLeft, ChevronRight, Eye, CheckCircle, Send,
  Download, MessageCircle, FileText, AlertTriangle, Info, Share2
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import { InvoiceForm } from '../components/invoice';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const Invoices = () => {
  const { language } = useLanguage();
  const { user, isPlanExpired } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  // GST Profile check
  const [showGstWarning, setShowGstWarning] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (filterStatus) params.append('status', filterStatus);
      const res = await axios.get(`${API_URL}/api/invoices?${params}`, { withCredentials: true });
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
    } catch (err) {
      toast.error(language === 'hi' ? 'इनवॉइस लोड करने में विफल' : 'Failed to load invoices');
    }
    setLoading(false);
  }, [page, filterStatus, language]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Check GST profile
  const checkGstProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/gst/check-profile`, { withCredentials: true });
      setBusinessProfile(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  };

  // Open form for new invoice
  const openForm = async () => {
    // Check plan expiry before allowing to create invoice
    if (isPlanExpired()) {
      setShowPlanExpired(true);
      return;
    }
    
    const profile = await checkGstProfile();

    if (!profile || !profile.has_gst) {
      setShowGstWarning(true);
      return;
    }

    setBusinessProfile(profile);
    setShowForm(true);
  };

  // Update status
  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/invoices/${id}/status`, { status }, { withCredentials: true });
      toast.success(language === 'hi' ? 'स्थिति अपडेट की गई' : 'Status updated');
      fetchInvoices();
      if (viewInvoice?.invoice_id === id) setViewInvoice({ ...viewInvoice, status });
    } catch (err) { toast.error('Failed'); }
  };

  // Delete invoice
  const handleDelete = async (id) => {
    if (!window.confirm(language === 'hi' ? 'क्या आप इस इनवॉइस को हटाना चाहते हैं?' : 'Delete this invoice?')) return;
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`, { withCredentials: true });
      toast.success(language === 'hi' ? 'इनवॉइस हटाया गया' : 'Invoice deleted');
      fetchInvoices();
    } catch (err) { toast.error('Failed'); }
  };

  // Download PDF
  const downloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/gst-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'hi' ? 'PDF डाउनलोड हुआ' : 'PDF downloaded');
    } catch (err) {
      try {
        const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
          withCredentials: true,
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${invoiceNumber}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(language === 'hi' ? 'PDF डाउनलोड हुआ' : 'PDF downloaded');
      } catch (e) {
        toast.error(language === 'hi' ? 'PDF डाउनलोड विफल' : 'Failed to download PDF');
      }
    }
  };

  // Share on WhatsApp
  const shareWhatsApp = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/gst-whatsapp`, { withCredentials: true });
      window.open(res.data.whatsapp_url, '_blank');
    } catch (err) {
      try {
        const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/whatsapp-share`, { withCredentials: true });
        window.open(res.data.whatsapp_url, '_blank');
      } catch (e) {
        toast.error(language === 'hi' ? 'शेयर लिंक विफल' : 'Failed to generate share link');
      }
    }
  };

  // OS Native Share (with PDF attachment)
  const shareNative = async (invoiceId, invoiceNumber) => {
    try {
      // First download the PDF blob
      const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/gst-pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      
      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const file = new File([pdfBlob], `${invoiceNumber}.pdf`, { type: 'application/pdf' });
      
      // Check if Web Share API supports files
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Invoice ${invoiceNumber}`,
          text: `Invoice ${invoiceNumber} from ${user?.business_name || 'Business'}`,
          files: [file],
        });
        toast.success(language === 'hi' ? 'शेयर किया गया' : 'Shared successfully');
      } else if (navigator.share) {
        // Fallback: Share without file
        await navigator.share({
          title: `Invoice ${invoiceNumber}`,
          text: `Invoice ${invoiceNumber} from ${user?.business_name || 'Business'}. Please contact us for the PDF.`,
        });
      } else {
        // No Web Share API - show download option
        toast.info(language === 'hi' ? 'शेयर API उपलब्ध नहीं। PDF डाउनलोड करें।' : 'Share not supported. Download PDF instead.');
        downloadPDF(invoiceId, invoiceNumber);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled share
        return;
      }
      console.error('Share error:', err);
      toast.error(language === 'hi' ? 'शेयर विफल' : 'Failed to share');
    }
  };

  const totalPages = Math.ceil(total / 20);

  const invBadge = (
    <span className="bg-[var(--aether-surface-high)] text-white/65 px-2 py-0.5 rounded text-[12px] font-bold">{total}</span>
  );
  const invAddBtn = (
    <Button onClick={openForm} className="bg-[#021945] hover:bg-[#021945]/90 text-xs sm:text-sm whitespace-nowrap" data-testid="create-invoice-btn">
      <Plus className="h-4 w-4 sm:mr-1" />
      <span className="hidden sm:inline">{language === 'hi' ? 'नया इनवॉइस' : 'New Invoice'}</span>
      <span className="sm:hidden">{language === 'hi' ? 'नया' : 'New'}</span>
    </Button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'इनवॉइस' : 'Invoices'}
      icon={Receipt}
      iconColor="#ffffff"
      titleTestId="invoices-title"
      badge={invBadge}
      rightElement={invAddBtn}
      mainClassName="p-3 sm:p-4 md:p-8 space-y-5"
    >
          {/* Page intro */}
          <div className="hidden md:block">
            <p className="label-caps text-white/55 mb-1">{language === 'hi' ? 'इनवॉइस' : 'BILLING & INVOICES'}</p>
            <p className="text-white/55 text-sm">{language === 'hi' ? 'अपने बिलिंग चक्र और बकाया भुगतान का प्रबंधन करें।' : 'Manage your billing cycles, outstanding payments, and financial records.'}</p>
          </div>

          {/* Status Filters — Aether tabs */}
          <div className="flex border-b border-[var(--aether-outline-variant)] mb-2 overflow-x-auto no-scrollbar">
            {['', 'draft', 'sent', 'paid', 'cancelled'].map(s => (
              <button key={s}
                onClick={() => { setFilterStatus(s); setPage(1); }}
                className={`px-4 sm:px-6 py-3 border-b-2 font-bold text-xs sm:text-sm capitalize transition-colors whitespace-nowrap ${
                  filterStatus === s
                    ? 'border-white text-white'
                    : 'border-transparent text-white/55 hover:text-white'
                }`}
                data-testid={`filter-${s || 'all'}`}>
                {s || (language === 'hi' ? 'सभी' : 'All')}
              </button>
            ))}
          </div>

          {/* Invoice List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-white/70 border-t-transparent rounded-full" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[var(--aether-outline-variant)] rounded-xl">
              <Receipt className="h-12 w-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/55">{language === 'hi' ? 'कोई इनवॉइस नहीं' : 'No invoices found'}</p>
              <p className="text-sm text-white/35 mt-2">{language === 'hi' ? '"नया इनवॉइस" क्लिक करें' : 'Click "New Invoice" to create one'}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="invoice-list">
              {invoices.map((inv) => {
                const statusStripe = inv.status === 'paid' ? '#62FF96'
                                    : inv.status === 'sent' ? '#3B82F6'
                                    : inv.status === 'draft' ? '#9CA3AF'
                                    : inv.status === 'cancelled' ? '#EF4444' : '#9CA3AF';
                return (
                <div
                  key={inv.invoice_id}
                  className="glass-card glass-card-hoverable executive-border p-4 sm:p-5 border-l-4 group"
                  style={{ borderLeftColor: statusStripe }}
                  data-testid={`invoice-card-${inv.invoice_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-white tracking-tight text-base">{inv.invoice_number}</span>
                        <span className="label-caps text-[10px] px-2 py-0.5 rounded uppercase" style={{ backgroundColor: `${statusStripe}1F`, color: statusStripe }}>{inv.status}</span>
                        {inv.is_gst_invoice && (
                          <span className="label-caps text-[10px] px-2 py-0.5 rounded bg-[#62FF96]/10 text-[#62FF96] uppercase">GST</span>
                        )}
                      </div>
                      {inv.customer_name && <p className="text-sm text-white/75 truncate">{inv.customer_name}</p>}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/45 mt-1">
                        <span className="headline-sm text-white">₹{inv.total_amount?.toLocaleString('en-IN')}</span>
                        <span>{inv.invoice_date || new Date(inv.created_at).toLocaleDateString('en-IN')}</span>
                        {inv.customer_state && <span className="hidden sm:inline">{inv.customer_state}</span>}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1 justify-end text-white/55 opacity-70 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.invoice_id}`}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-white hover:bg-white/5">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button onClick={() => downloadPDF(inv.invoice_id, inv.invoice_number)} data-testid={`download-invoice-${inv.invoice_id}`} title="Download PDF"
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-white hover:bg-white/5">
                        <Download className="h-4 w-4" />
                      </button>
                      <button onClick={() => shareWhatsApp(inv.invoice_id)} data-testid={`whatsapp-invoice-${inv.invoice_id}`} title="Share on WhatsApp"
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-[#62FF96] hover:bg-[#62FF96]/10">
                        <MessageCircle className="h-4 w-4" />
                      </button>
                      <button onClick={() => shareNative(inv.invoice_id, inv.invoice_number)} data-testid={`share-invoice-${inv.invoice_id}`} title="Share"
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-white hover:bg-white/5">
                        <Share2 className="h-4 w-4" />
                      </button>
                      {inv.status === 'draft' && (
                        <button onClick={() => updateStatus(inv.invoice_id, 'sent')}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-blue-300 hover:bg-blue-500/10">
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      {inv.status === 'sent' && (
                        <button onClick={() => updateStatus(inv.invoice_id, 'paid')}
                          className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-[#62FF96] hover:bg-[#62FF96]/10">
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(inv.invoice_id)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:text-red-400 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="flex items-center text-sm text-slate-600">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

      {/* GST Warning Dialog */}
      <Dialog open={showGstWarning} onOpenChange={setShowGstWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              {language === 'hi' ? 'GST नंबर आवश्यक' : 'GST Number Required'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              {language === 'hi'
                ? 'GST इनवॉइस बनाने के लिए, कृपया अपना GST नंबर प्रोफाइल → व्यवसाय विवरण में जोड़ें।'
                : 'To generate GST invoices, please add your GST number in Profile → Business Details.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGstWarning(false)}>
              {language === 'hi' ? 'रद्द करें' : 'Cancel'}
            </Button>
            <Button
              className="bg-[#021945] hover:bg-[#021945]/90"
              onClick={() => {
                setShowGstWarning(false);
                navigate('/profile');
              }}
            >
              {language === 'hi' ? 'प्रोफाइल पर जाएं' : 'Go to Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Form Dialog (Refactored Component) */}
      <InvoiceForm
        open={showForm}
        onOpenChange={setShowForm}
        onSuccess={fetchInvoices}
        businessProfile={businessProfile}
      />

      {/* View Invoice Dialog */}
      <Dialog open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {viewInvoice?.invoice_number}
              {viewInvoice?.is_gst_invoice && (
                <Badge className="ml-2 bg-green-50 text-green-700 border-green-200">GST Invoice</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewInvoice && (
            <div className="space-y-4">
              {/* Status and Actions */}
              <div className="flex justify-between items-center">
                <Badge className={`${statusColors[viewInvoice.status] || ''}`}>{viewInvoice.status}</Badge>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadPDF(viewInvoice.invoice_id, viewInvoice.invoice_number)} data-testid="view-download-pdf">
                    <Download className="h-4 w-4 mr-1" />PDF
                  </Button>
                  <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => shareWhatsApp(viewInvoice.invoice_id)} data-testid="view-whatsapp-share">
                    <MessageCircle className="h-4 w-4 mr-1" />WhatsApp
                  </Button>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Date:</span> {viewInvoice.invoice_date || new Date(viewInvoice.created_at).toLocaleDateString('en-IN')}</div>
                  {viewInvoice.due_date && <div><span className="text-slate-500">Due:</span> {viewInvoice.due_date}</div>}
                  {viewInvoice.customer_state && (
                    <div><span className="text-slate-500">Place of Supply:</span> {viewInvoice.customer_state}</div>
                  )}
                  {viewInvoice.is_interstate !== undefined && (
                    <div><span className="text-slate-500">Type:</span> {viewInvoice.is_interstate ? 'Interstate (IGST)' : 'Intrastate (CGST+SGST)'}</div>
                  )}
                </div>
              </div>

              {/* Business Info */}
              {viewInvoice.business_name && (
                <div className="text-sm bg-green-50 p-3 rounded-lg">
                  <p className="font-semibold text-green-700 mb-1">{language === 'hi' ? 'विक्रेता:' : 'From:'}</p>
                  <p className="font-medium">{viewInvoice.business_name}</p>
                  {viewInvoice.business_gstin && <p className="text-green-600">GSTIN: {viewInvoice.business_gstin}</p>}
                  {viewInvoice.business_state && <p className="text-green-600">{viewInvoice.business_state}</p>}
                </div>
              )}

              {/* Customer Info */}
              {viewInvoice.customer_name && (
                <div className="text-sm bg-slate-50 p-3 rounded-lg">
                  <p className="font-semibold text-slate-700 mb-1">{language === 'hi' ? 'ग्राहक:' : 'Bill To:'}</p>
                  <p className="font-medium">{viewInvoice.customer_name}</p>
                  {viewInvoice.customer_address && <p className="text-slate-500">{viewInvoice.customer_address}</p>}
                  {viewInvoice.customer_state && <p className="text-slate-500">State: {viewInvoice.customer_state}</p>}
                  {viewInvoice.customer_gstin && <p className="text-slate-500">GSTIN: {viewInvoice.customer_gstin}</p>}
                  {viewInvoice.customer_mobile && <p className="text-slate-500">Phone: {viewInvoice.customer_mobile}</p>}
                </div>
              )}

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2">{language === 'hi' ? 'विवरण' : 'Description'}</th>
                      <th className="text-right p-2">{language === 'hi' ? 'मात्रा' : 'Qty'}</th>
                      <th className="text-right p-2">{language === 'hi' ? 'दर' : 'Rate'}</th>
                      <th className="text-right p-2">GST%</th>
                      <th className="text-right p-2">{language === 'hi' ? 'कुल' : 'Total'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.items?.map((item, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          {item.description || item.item_name}
                          {item.hsn_sac && <span className="text-xs text-slate-400 ml-1">(HSN: {item.hsn_sac})</span>}
                        </td>
                        <td className="text-right p-2">{item.quantity} {item.unit}</td>
                        <td className="text-right p-2">Rs.{item.unit_price?.toLocaleString('en-IN')}</td>
                        <td className="text-right p-2">{item.gst_percent || item.tax_percent || 0}%</td>
                        <td className="text-right p-2">Rs.{item.total?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="text-right space-y-1 text-sm bg-slate-50 p-3 rounded-lg">
                <p><span className="text-slate-500">{language === 'hi' ? 'उप-योग:' : 'Subtotal:'}</span> Rs.{viewInvoice.subtotal?.toFixed(2)}</p>
                {viewInvoice.discount_amount > 0 && (
                  <>
                    <p className="text-amber-600">
                      <span>{language === 'hi' ? 'छूट:' : 'Discount:'}</span> -Rs.{viewInvoice.discount_amount?.toFixed(2)}
                    </p>
                    <p><span className="text-slate-500">{language === 'hi' ? 'कर योग्य:' : 'Taxable:'}</span> Rs.{viewInvoice.taxable_amount?.toFixed(2)}</p>
                  </>
                )}
                {viewInvoice.is_interstate ? (
                  <p><span className="text-slate-500">IGST:</span> Rs.{viewInvoice.igst_total?.toFixed(2)}</p>
                ) : viewInvoice.cgst_total !== undefined ? (
                  <>
                    <p><span className="text-slate-500">CGST:</span> Rs.{viewInvoice.cgst_total?.toFixed(2)}</p>
                    <p><span className="text-slate-500">SGST:</span> Rs.{viewInvoice.sgst_total?.toFixed(2)}</p>
                  </>
                ) : (
                  <p><span className="text-slate-500">{language === 'hi' ? 'कर:' : 'Tax:'}</span> Rs.{viewInvoice.tax_amount?.toFixed(2)}</p>
                )}
                {viewInvoice.round_off !== undefined && viewInvoice.round_off !== 0 && (
                  <p><span className="text-slate-500">{language === 'hi' ? 'राउंड ऑफ:' : 'Round Off:'}</span> {viewInvoice.round_off > 0 ? '+' : ''}Rs.{viewInvoice.round_off?.toFixed(2)}</p>
                )}
                <p className="text-lg font-bold pt-2 border-t border-slate-200">
                  <span className="text-[#d7263d]">Rs.{viewInvoice.total_amount?.toLocaleString('en-IN')}</span>
                </p>
                {viewInvoice.amount_in_words && (
                  <p className="text-xs text-slate-500 italic">{viewInvoice.amount_in_words}</p>
                )}
              </div>

              {/* Notes */}
              {viewInvoice.notes && (
                <div className="text-sm text-slate-500">
                  <span className="font-medium">{language === 'hi' ? 'नोट्स:' : 'Notes:'}</span> {viewInvoice.notes}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </AetherPage>
  );
};

export default Invoices;
